import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import validator from 'email-validator';
import SessionStore from 'stores/SessionStore';
import { getDeviceHash } from 'utils/browser';
import { Button } from 'react-bootstrap';
import Alert from 'components/ui/Alert';
import Table from 'components/v2/Table';
import Label from 'components/v2/forms/Label';
import TextField from 'components/v2/forms/TextField';
import ExternalLink from 'components/v2/ExternalLink';
import DocumentsWebAPIUtils from 'utils/DocumentsWebAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import CargomaticCard from 'components/v2/card';
import AccountingWebAPIUtils from 'utils/AccountingWebAPIUtils';
import Pagination from 'components/v2/PaginationV2';
import { showToast } from 'stores/ToastStore';

const TOO_MANY_DOCUMENTS = 'TOO_MANY_DOCUMENTS';
const NO_DOCUMENTS_FOUND_ERROR = 'NO_DOCUMENTS_FOUND';
const INVOICES_PER_PAGE = 20;

export default function InvoiceResults({ invoiceNumbers, handleCancel }) {
  const [invoices, setInvoices] = useState([]);
  const [searching, setSearching] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [emails, setEmails] = useState();
  const [emailsInvalid, setEmailsInvalid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showMixedShipmentTypeAlert, setShowMixedShipmentTypeAlert] =
    useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      setInvoices([]);
      setSearching(true);
      setNoResults(false);

      let query = {
        number: invoiceNumbers,
        page,
        page_size: INVOICES_PER_PAGE,
      };

      try {
        const res = await AccountingWebAPIUtils.getAdminInvoices(query);
        const results = res.entity?.invoices || [];
        const statsRes =
          await AccountingWebAPIUtils.getAdminInvoicesStats(query);
        const stats = statsRes.entity || [];
        const count = stats.reduce((total, item) => total + item.count, 0);

        setInvoices(results);
        setShowMixedShipmentTypeAlert(
          results.some((item) => item?.type === 'drayage') &&
            results.some((item) => item?.type !== 'drayage'),
        );
        setTotalPages(Math.ceil(count / INVOICES_PER_PAGE));

        if (results.length === 0) {
          setNoResults(true);
        }

        setSearching(false);
      } catch (e) {
        console.log('Error', e.body);

        setSearching(false);
        setNoResults(true);
      }
    })();
  }, [invoiceNumbers, page]);

  const headers = [
    {
      field: 'number',
      label: t('INVOICE_NUMBER'),
    },
    {
      field: 'shipper',
      label: t('SHIPPER'),
    },
  ];

  const columns = [
    {
      field: 'number',
      render: (invoiceNumber, row) => {
        return (
          <ExternalLink
            href={`/invoices/${row._id}`}
            title={invoiceNumber}
            showIcon
          />
        );
      },
    },
    {
      field: 'shipper',
      render: (shipper, row) => {
        return (
          <ExternalLink
            href={`/users/${shipper}`}
            title={row.shipper_name}
            showIcon
          />
        );
      },
    },
  ];

  const handleEmailsChange = (value) => {
    const emails = value.replace(/' '/g, '').split(',');
    const invalidEmailAddresses = emails.filter(
      (ea) => !validator.validate(ea),
    );
    if (invalidEmailAddresses?.length > 0) {
      setEmailsInvalid(true);
    } else {
      setEmailsInvalid(false);
    }
    setEmails(value);
  };

  const handleDownload = async () => {
    try {
      setLoading(true);

      const deviceHash = getDeviceHash();
      const languageCodeId =
        SessionStore.getLanguageCodeIdPreference(deviceHash) || 'en-us';

      await DocumentsWebAPIUtils.bulkDownloadInvoicesByNumbers(
        invoiceNumbers,
        emails,
        languageCodeId,
      );

      showToast(t('DOCUMENTS_SENT_VIA_EMAIL'), null, 'success');
    } catch (e) {
      if (e.entity?.error === NO_DOCUMENTS_FOUND_ERROR) {
        showToast(
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          t('NO_DOCUMENTS_ARE_FOUND_FOR_DOWNLOAD'),
          'error',
        );
      } else if (e.entity?.error === TOO_MANY_DOCUMENTS) {
        showToast(
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          t('TOO_MANY_DOCUMENTS_PLEASE_FORMULATE_A_MORE_PRECISE_REQUEST'),
          'error',
        );
      } else {
        showToast(
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          t('DOWNLOAD_DOCUMENTS_ERROR'),
          'error',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const renderMixedShipmentTypeAlert = () => (
    <Alert
      className="flex-1 mb-2"
      type="info"
      title={t('DOUBLE_INVOICE_TYPE_EXPORT_EMAIL_INFO')}
    />
  );

  return (
    <>
      <h3 className="mb-4">{t('SELECTED_INVOICES')}</h3>
      <div className="overflow-x-auto table-wrapper max-w-xl">
        <Table headers={headers} columns={columns} data={invoices} />
        {searching && (
          <div
            className="mt-4 pb-2 flex flex-col items-center"
            data-testid="searchingMessage"
          >
            <LoadingCircle color="white" text={t('LOADING')} />
          </div>
        )}
        {noResults && (
          <div className="mt-4 pb-2 text-center" data-testid="noResultsMessage">
            {t('NO_RESULTS_TO_DISPLAY')}
          </div>
        )}
        {totalPages > 1 && (
          <Pagination
            page={page}
            pages={totalPages}
            perPage={INVOICES_PER_PAGE}
            onPageClick={setPage}
          />
        )}
      </div>
      <CargomaticCard className="mt-4 max-w-xl">
        <div data-testid="downloadForm">
          <h3 className="mt-0">{t('EMAIL_NOTIFICATIONS')}</h3>
          {showMixedShipmentTypeAlert && renderMixedShipmentTypeAlert()}
          <div className="mt-4 sm:w-80 w-full">
            <Label htmlFor="emails" className="">
              {t('EMAILS')}
            </Label>
            {emailsInvalid && (
              <Alert type="warning">{t('VALID_EMAIL_ONLY')}</Alert>
            )}
            <TextField
              type="text"
              name="emails"
              id="emails"
              value={emails || ''}
              placeholder={t('ENTER_EMAILS_SEPARATED_BY_COMMAS')}
              disabled={loading}
              onChange={handleEmailsChange}
            />
          </div>
          <div className="sm:flex sm:justify-end sm:space-x-4 mt-8 flex-wrap">
            {loading && (
              <div data-testid="loadingMessage">
                <LoadingCircle color="white" text={t('LOADING')} />
              </div>
            )}
            <Button
              data-testid="downloadButton"
              bsStyle="green"
              className="mb-4 sm:mb-0"
              onClick={handleDownload}
              disabled={
                noResults || searching || loading || !emails || emailsInvalid
              }
            >
              {t('REGENERATE_AND_EMAIL_INVOICES')}
            </Button>
            <Button
              data-testid="cancelButton"
              onClick={handleCancel}
              disabled={loading}
            >
              {t('CANCEL')}
            </Button>
          </div>
        </div>
      </CargomaticCard>
    </>
  );
}

InvoiceResults.propTypes = {
  invoiceNumbers: PropTypes.arrayOf(PropTypes.string),
  handleCancel: PropTypes.func,
};
